import { ColumnInfo } from './dataAnalyzer';

export interface QAResult {
  question: string;
  answer: string;
  confidence: 'high' | 'medium' | 'low';
  canAnswer: boolean;
  reasoning?: string;
  supportingData?: any;
  citations?: Array<{ label: string; detail: string }>;
  suggestions?: string[];
}

export interface QuestionSuggestion {
  category: string;
  questions: string[];
  icon: string;
}

export class DataQAEngine {
  private data: any[];
  private headers: string[];
  private columnInfo: Map<string, ColumnInfo>;

  constructor(data: any[], headers: string[], columnInfo: Map<string, ColumnInfo>) {
    this.data = data;
    this.headers = headers;
    this.columnInfo = columnInfo;
  }

  answerQuestion(question: string): QAResult {
    const normalizedQuestion = question.toLowerCase().trim();
    
    // Classify the question type and check if we can answer it
    const questionType = this.classifyQuestion(normalizedQuestion);
    
    if (!questionType.canAnswer) {
      return {
        question,
        answer: questionType.reason || "I don't have enough data to answer this question.",
        confidence: 'low',
        canAnswer: false,
        reasoning: questionType.reason,
        suggestions: this.getSuggestions(normalizedQuestion)
      };
    }

    // Try to answer based on question type
    try {
      const result = this.processQuestion(normalizedQuestion, questionType.type);
      return {
        question,
        ...result,
        canAnswer: true,
        citations: this.buildCitationsFromSupportingData(result.supportingData)
      };
    } catch (error) {
      return {
        question,
        answer: "I couldn't process this question with the available data.",
        confidence: 'low',
        canAnswer: false,
        suggestions: this.getSuggestions(normalizedQuestion)
      };
    }
  }

  private classifyQuestion(question: string): { canAnswer: boolean; type?: string; reason?: string } {
    // Check for basic data availability
    if (this.data.length === 0) {
      return { canAnswer: false, reason: "No data available to answer questions." };
    }

    // Count and total questions
    if (this.isCountQuestion(question)) {
      return { canAnswer: true, type: 'count' };
    }

    // Average/mean questions
    if (this.isAverageQuestion(question)) {
      const numericColumns = this.getNumericColumns();
      if (numericColumns.length === 0) {
        return { canAnswer: false, reason: "No numeric data available to calculate averages." };
      }
      return { canAnswer: true, type: 'average' };
    }

    // Maximum/minimum questions
    if (this.isMinMaxQuestion(question)) {
      const numericColumns = this.getNumericColumns();
      if (numericColumns.length === 0) {
        return { canAnswer: false, reason: "No numeric data available to find min/max values." };
      }
      return { canAnswer: true, type: 'minmax' };
    }

    // Top/best/most questions
    if (this.isTopQuestion(question)) {
      return { canAnswer: true, type: 'top' };
    }

    // Comparison questions
    if (this.isComparisonQuestion(question)) {
      return { canAnswer: true, type: 'comparison' };
    }

    // Distribution/percentage questions
    if (this.isDistributionQuestion(question)) {
      return { canAnswer: true, type: 'distribution' };
    }

    // Trend questions
    if (this.isTrendQuestion(question)) {
      const dateColumns = this.getDateColumns();
      if (dateColumns.length === 0) {
        return { canAnswer: false, reason: "No date/time data available to analyze trends." };
      }
      return { canAnswer: true, type: 'trend' };
    }

    // Correlation questions
    if (this.isCorrelationQuestion(question)) {
      const numericColumns = this.getNumericColumns();
      if (numericColumns.length < 2) {
        return { canAnswer: false, reason: "Need at least two numeric columns to analyze relationships." };
      }
      return { canAnswer: true, type: 'correlation' };
    }

    // Filter/subset questions
    if (this.isFilterQuestion(question)) {
      return { canAnswer: true, type: 'filter' };
    }

    return { canAnswer: false, reason: "This question type is not supported with the current data." };
  }

  private processQuestion(question: string, type: string | undefined): Omit<QAResult, 'question' | 'canAnswer'> {
    if (!type) {
      throw new Error('Unknown question type');
    }
    switch (type) {
      case 'count':
        return this.answerCountQuestion(question);
      case 'average':
        return this.answerAverageQuestion(question);
      case 'minmax':
        return this.answerMinMaxQuestion(question);
      case 'top':
        return this.answerTopQuestion(question);
      case 'comparison':
        return this.answerComparisonQuestion(question);
      case 'distribution':
        return this.answerDistributionQuestion(question);
      case 'trend':
        return this.answerTrendQuestion(question);
      case 'correlation':
        return this.answerCorrelationQuestion(question);
      case 'filter':
        return this.answerFilterQuestion(question);
      default:
        throw new Error('Unknown question type');
    }
  }

  private buildCitationsFromSupportingData(supportingData?: any): Array<{ label: string; detail: string }> {
    if (!supportingData) return [];
    const citations: Array<{ label: string; detail: string }> = [];

    if (supportingData.column) {
      citations.push({
        label: `Column: ${supportingData.column}`,
        detail: 'Computed from parsed dataset values.'
      });
    }

    if (typeof supportingData.count === 'number' || typeof supportingData.total === 'number') {
      citations.push({
        label: 'Row aggregate',
        detail: `Rows included: ${supportingData.count ?? supportingData.total}`
      });
    }

    if (typeof supportingData.average === 'number') {
      citations.push({
        label: 'Average calculation',
        detail: `Arithmetic mean = ${this.formatNumber(supportingData.average)}`
      });
    }

    if (typeof supportingData.value === 'number') {
      citations.push({
        label: 'Metric value',
        detail: `Computed value = ${this.formatNumber(supportingData.value)}`
      });
    }

    return citations;
  }

  private answerCountQuestion(question: string): Omit<QAResult, 'question' | 'canAnswer'> {
    const totalRecords = this.data.length;
    
    // Check for specific column mentions
    const mentionedColumn = this.findMentionedColumn(question);
    
    if (mentionedColumn) {
      const columnInfo = this.columnInfo.get(mentionedColumn);
      if (columnInfo?.type === 'categorical') {
        const uniqueValues = new Set(this.data.map(row => row[mentionedColumn])).size;
        return {
          answer: `There are ${uniqueValues} unique ${this.formatColumnName(mentionedColumn).toLowerCase()} values in the data.`,
          confidence: 'high',
          supportingData: { column: mentionedColumn, uniqueCount: uniqueValues, total: totalRecords }
        };
      }
    }

    // Default total count
    const recordType = this.inferRecordType();
    return {
      answer: `There are ${this.formatNumber(totalRecords)} ${recordType} in the dataset.`,
      confidence: 'high',
      supportingData: { total: totalRecords, type: recordType }
    };
  }

  private answerAverageQuestion(question: string): Omit<QAResult, 'question' | 'canAnswer'> {
    const mentionedColumn = this.findMentionedColumn(question);
    const numericColumns = this.getNumericColumns();
    
    let targetColumn = mentionedColumn;
    if (!targetColumn || !numericColumns.includes(targetColumn)) {
      // Find the most relevant numeric column
      targetColumn = this.findRelevantNumericColumn(question, numericColumns);
    }

    if (!targetColumn) {
      targetColumn = numericColumns[0]; // Fallback to first numeric column
    }

    if (!targetColumn) {
      return {
        answer: "No numeric columns available for average calculation.",
        confidence: 'low'
      };
    }

    const values = this.data
      .map(row => targetColumn ? Number(row[targetColumn]) : NaN)
      .filter(val => !isNaN(val));

    if (values.length === 0) {
      return {
        answer: `No valid numeric data found in ${this.formatColumnName(targetColumn)}.`,
        confidence: 'low'
      };
    }

    const average = values.reduce((sum, val) => sum + val, 0) / values.length;
    const columnName = this.formatColumnName(targetColumn);

    return {
      answer: `The average ${columnName.toLowerCase()} is ${this.formatNumber(average)}.`,
      confidence: 'high',
      supportingData: { column: targetColumn, average, count: values.length }
    };
  }

  private answerMinMaxQuestion(question: string): Omit<QAResult, 'question' | 'canAnswer'> {
    const isMax = question.includes('max') || question.includes('highest') || question.includes('largest');
    const mentionedColumn = this.findMentionedColumn(question);
    const numericColumns = this.getNumericColumns();
    
    let targetColumn = mentionedColumn;
    if (!targetColumn || !numericColumns.includes(targetColumn)) {
      targetColumn = this.findRelevantNumericColumn(question, numericColumns);
    }

    if (!targetColumn) {
      targetColumn = numericColumns[0];
    }

    if (!targetColumn) {
      return {
        answer: "No numeric columns available for min/max calculation.",
        confidence: 'low'
      };
    }

    const values = this.data
      .map(row => targetColumn ? Number(row[targetColumn]) : NaN)
      .filter(val => !isNaN(val));

    if (values.length === 0) {
      return {
        answer: `No valid numeric data found in ${this.formatColumnName(targetColumn)}.`,
        confidence: 'low'
      };
    }

    const result = isMax ? Math.max(...values) : Math.min(...values);
    const columnName = this.formatColumnName(targetColumn);
    const type = isMax ? 'highest' : 'lowest';

    return {
      answer: `The ${type} ${columnName.toLowerCase()} is ${this.formatNumber(result)}.`,
      confidence: 'high',
      supportingData: { column: targetColumn, value: result, type }
    };
  }

  private answerTopQuestion(question: string): Omit<QAResult, 'question' | 'canAnswer'> {
    const mentionedColumn = this.findMentionedColumn(question);
    const categoricalColumns = this.getCategoricalColumns();
    
    let targetColumn = mentionedColumn;
    if (!targetColumn || !categoricalColumns.includes(targetColumn)) {
      // Find the most relevant categorical column
      targetColumn = this.findRelevantCategoricalColumn(question, categoricalColumns);
    }

    if (!targetColumn) {
      targetColumn = categoricalColumns[0];
    }

    if (!targetColumn) {
      return {
        answer: "No categorical columns available for analysis.",
        confidence: 'low'
      };
    }

    const counts = this.data.reduce((acc, row) => {
      const value = targetColumn ? row[targetColumn] : '';
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const sortedEntries = Object.entries(counts)
      .sort(([,a], [,b]) => (b as number) - (a as number));

    if (sortedEntries.length === 0) {
      return {
        answer: `No data found in ${this.formatColumnName(targetColumn)}.`,
        confidence: 'low'
      };
    }

    const [topValue, topCount] = sortedEntries[0];
    const percentage = ((topCount as number) / this.data.length * 100).toFixed(1);
    const columnName = this.formatColumnName(targetColumn);

    return {
      answer: `The top ${columnName.toLowerCase()} is "${topValue}" with ${topCount} occurrences (${percentage}% of total).`,
      confidence: 'high',
      supportingData: { column: targetColumn, topValue, count: topCount, percentage }
    };
  }

  private answerComparisonQuestion(question: string): Omit<QAResult, 'question' | 'canAnswer'> {
    // Extract potential comparison values from the question
    const mentionedColumn = this.findMentionedColumn(question);
    
    if (!mentionedColumn) {
      return {
        answer: "I need you to specify which column or field you want to compare.",
        confidence: 'low'
      };
    }

    const columnInfo = this.columnInfo.get(mentionedColumn);
    
    if (columnInfo?.type === 'categorical') {
      const counts = this.data.reduce((acc, row) => {
        const value = row[mentionedColumn];
        acc[value] = (acc[value] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const sortedEntries = Object.entries(counts)
        .sort(([,a], [,b]) => (b as number) - (a as number))
        .slice(0, 3);

      const comparison = sortedEntries.map(([value, count], index) => {
        const percentage = ((count as number) / this.data.length * 100).toFixed(1);
        return `${index + 1}. ${value}: ${count} (${percentage}%)`;
      }).join('\n');

      return {
        answer: `Here's the breakdown for ${this.formatColumnName(mentionedColumn)}:\n${comparison}`,
        confidence: 'high',
        supportingData: { column: mentionedColumn, breakdown: sortedEntries }
      };
    }

    return {
      answer: `${this.formatColumnName(mentionedColumn)} appears to be numeric. Try asking for average, min/max, or specific value comparisons.`,
      confidence: 'medium'
    };
  }

  private answerDistributionQuestion(question: string): Omit<QAResult, 'question' | 'canAnswer'> {
    const mentionedColumn = this.findMentionedColumn(question);
    
    if (!mentionedColumn) {
      return {
        answer: "Please specify which field you want to see the distribution for.",
        confidence: 'low'
      };
    }

    const columnInfo = this.columnInfo.get(mentionedColumn);
    
    if (columnInfo?.type === 'categorical') {
      const counts = this.data.reduce((acc, row) => {
        const value = row[mentionedColumn];
        acc[value] = (acc[value] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const total = this.data.length;
      const distribution = Object.entries(counts)
        .sort(([,a], [,b]) => (b as number) - (a as number))
        .slice(0, 5)
        .map(([value, count]) => {
          const percentage = ((count as number) / total * 100).toFixed(1);
          return `${value}: ${percentage}%`;
        }).join(', ');

      return {
        answer: `Distribution of ${this.formatColumnName(mentionedColumn)}: ${distribution}`,
        confidence: 'high',
        supportingData: { column: mentionedColumn, distribution: counts }
      };
    }

    return {
      answer: `${this.formatColumnName(mentionedColumn)} is numeric. Try asking for ranges or averages instead.`,
      confidence: 'medium'
    };
  }

  private answerTrendQuestion(question: string): Omit<QAResult, 'question' | 'canAnswer'> {
    const dateColumns = this.getDateColumns();
    const numericColumns = this.getNumericColumns();
    
    if (dateColumns.length === 0 || numericColumns.length === 0) {
      return {
        answer: "No time-series data available to analyze trends.",
        confidence: 'low'
      };
    }

    const dateColumn = dateColumns[0];
    const valueColumn = this.findMentionedColumn(question) || numericColumns[0];

    // Sort data by date and calculate trend
    const timeData = this.data
      .filter(row => row[dateColumn] && !isNaN(Number(row[valueColumn])))
      .sort((a, b) => new Date(a[dateColumn]).getTime() - new Date(b[dateColumn]).getTime());

    if (timeData.length < 2) {
      return {
        answer: "Not enough time-series data points to determine a trend.",
        confidence: 'low'
      };
    }

    const firstValue = Number(timeData[0][valueColumn]);
    const lastValue = Number(timeData[timeData.length - 1][valueColumn]);
    const change = ((lastValue - firstValue) / firstValue * 100).toFixed(1);

    const trend = lastValue > firstValue ? 'increasing' : lastValue < firstValue ? 'decreasing' : 'stable';
    const columnName = this.formatColumnName(valueColumn);

    return {
      answer: `${columnName} shows a ${trend} trend over time with a ${Math.abs(Number(change))}% ${trend === 'increasing' ? 'increase' : trend === 'decreasing' ? 'decrease' : 'change'}.`,
      confidence: 'high',
      supportingData: { column: valueColumn, trend, change, firstValue, lastValue }
    };
  }

  private answerCorrelationQuestion(question: string): Omit<QAResult, 'question' | 'canAnswer'> {
    const numericColumns = this.getNumericColumns();
    
    if (numericColumns.length < 2) {
      return {
        answer: "Need at least two numeric columns to analyze relationships.",
        confidence: 'low'
      };
    }

    // Try to find two columns mentioned in the question
    const mentionedColumns = numericColumns.filter(col => 
      question.includes(col.toLowerCase()) || 
      question.includes(this.formatColumnName(col).toLowerCase())
    );

    let col1 = mentionedColumns[0] || numericColumns[0];
    let col2 = mentionedColumns[1] || numericColumns[1];

    const correlation = this.calculateCorrelation(col1, col2);
    const strength = Math.abs(correlation) > 0.7 ? 'strong' : Math.abs(correlation) > 0.3 ? 'moderate' : 'weak';
    const direction = correlation > 0 ? 'positive' : 'negative';

    return {
      answer: `There is a ${strength} ${direction} relationship between ${this.formatColumnName(col1)} and ${this.formatColumnName(col2)} (correlation: ${correlation.toFixed(3)}).`,
      confidence: 'high',
      supportingData: { col1, col2, correlation, strength, direction }
    };
  }

  private answerFilterQuestion(question: string): Omit<QAResult, 'question' | 'canAnswer'> {
    // Extract filter conditions from the question
    const mentionedColumn = this.findMentionedColumn(question);
    
    if (!mentionedColumn) {
      return {
        answer: "Please specify which field you want to filter by.",
        confidence: 'low'
      };
    }

    // This is a simplified filter - in a real implementation, you'd parse more complex conditions
    const uniqueValues = Array.from(new Set(this.data.map(row => row[mentionedColumn])));
    
    return {
      answer: `Available ${this.formatColumnName(mentionedColumn).toLowerCase()} values: ${uniqueValues.slice(0, 10).join(', ')}${uniqueValues.length > 10 ? '...' : ''}`,
      confidence: 'medium',
      supportingData: { column: mentionedColumn, values: uniqueValues }
    };
  }

  // Question type detection methods
  private isCountQuestion(question: string): boolean {
    return /\b(how many|count|total|number of)\b/.test(question);
  }

  private isAverageQuestion(question: string): boolean {
    return /\b(average|mean|avg)\b/.test(question);
  }

  private isMinMaxQuestion(question: string): boolean {
    return /\b(min|max|minimum|maximum|highest|lowest|largest|smallest)\b/.test(question);
  }

  private isTopQuestion(question: string): boolean {
    return /\b(top|best|most|leading|popular)\b/.test(question);
  }

  private isComparisonQuestion(question: string): boolean {
    return /\b(compare|vs|versus|difference|between)\b/.test(question);
  }

  private isDistributionQuestion(question: string): boolean {
    return /\b(distribution|breakdown|percentage|proportion|split)\b/.test(question);
  }

  private isTrendQuestion(question: string): boolean {
    return /\b(trend|over time|growth|change|increase|decrease)\b/.test(question);
  }

  private isCorrelationQuestion(question: string): boolean {
    return /\b(relationship|correlation|related|connected|impact|affect)\b/.test(question);
  }

  private isFilterQuestion(question: string): boolean {
    return /\b(where|filter|show me|only|with|having)\b/.test(question);
  }

  // Utility methods
  private findMentionedColumn(question: string): string | null {
    for (const header of this.headers) {
      const variations = [
        header.toLowerCase(),
        this.formatColumnName(header).toLowerCase(),
        header.replace(/[_-]/g, ' ').toLowerCase()
      ];
      
      for (const variation of variations) {
        if (question.includes(variation)) {
          return header;
        }
      }
    }
    return null;
  }

  private getNumericColumns(): string[] {
    return Array.from(this.columnInfo.entries())
      .filter(([, info]) => info.type === 'numeric')
      .map(([name]) => name);
  }

  private getCategoricalColumns(): string[] {
    return Array.from(this.columnInfo.entries())
      .filter(([, info]) => info.type === 'categorical')
      .map(([name]) => name);
  }

  private getDateColumns(): string[] {
    return Array.from(this.columnInfo.entries())
      .filter(([, info]) => info.type === 'datetime')
      .map(([name]) => name);
  }

  private findRelevantNumericColumn(question: string, columns: string[]): string | null {
    // Priority keywords for different numeric columns
    const keywords = {
      'price': ['price', 'cost', 'amount', 'value'],
      'quantity': ['quantity', 'count', 'number', 'amount'],
      'rating': ['rating', 'score', 'review'],
      'revenue': ['revenue', 'sales', 'income'],
      'age': ['age', 'years'],
    };

    for (const [type, words] of Object.entries(keywords)) {
      if (words.some(word => question.includes(word))) {
        const match = columns.find(col => 
          col.toLowerCase().includes(type) || 
          words.some(word => col.toLowerCase().includes(word))
        );
        if (match) return match;
      }
    }

    return null;
  }

  private findRelevantCategoricalColumn(question: string, columns: string[]): string | null {
    const keywords = {
      'category': ['category', 'type', 'kind'],
      'region': ['region', 'location', 'area', 'country'],
      'department': ['department', 'division', 'team'],
      'status': ['status', 'state', 'condition'],
    };

    for (const [type, words] of Object.entries(keywords)) {
      if (words.some(word => question.includes(word))) {
        const match = columns.find(col => 
          col.toLowerCase().includes(type) || 
          words.some(word => col.toLowerCase().includes(word))
        );
        if (match) return match;
      }
    }

    return null;
  }

  private calculateCorrelation(col1: string, col2: string): number {
    const pairs = this.data
      .map(row => [Number(row[col1]), Number(row[col2])])
      .filter(([x, y]) => !isNaN(x) && !isNaN(y));

    if (pairs.length === 0) return 0;

    const n = pairs.length;
    const sumX = pairs.reduce((sum, [x]) => sum + x, 0);
    const sumY = pairs.reduce((sum, [, y]) => sum + y, 0);
    const sumXY = pairs.reduce((sum, [x, y]) => sum + x * y, 0);
    const sumX2 = pairs.reduce((sum, [x]) => sum + x * x, 0);
    const sumY2 = pairs.reduce((sum, [, y]) => sum + y * y, 0);

    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

    return denominator === 0 ? 0 : numerator / denominator;
  }

  private inferRecordType(): string {
    const headers = this.headers.map(h => h.toLowerCase());
    
    if (headers.some(h => h.includes('customer'))) return 'customers';
    if (headers.some(h => h.includes('order'))) return 'orders';
    if (headers.some(h => h.includes('product'))) return 'products';
    if (headers.some(h => h.includes('employee'))) return 'employees';
    if (headers.some(h => h.includes('sale'))) return 'sales';
    
    return 'records';
  }

  private formatColumnName(column: string): string {
    return column
      .replace(/[_-]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private formatNumber(num: number): string {
    if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    if (num % 1 === 0) return num.toString();
    return num.toFixed(2);
  }

  private getSuggestions(question: string): string[] {
    const suggestions = [];
    const numericCols = this.getNumericColumns();
    const categoricalCols = this.getCategoricalColumns();
    
    if (numericCols.length > 0) {
      suggestions.push(`What is the average ${this.formatColumnName(numericCols[0])}?`);
      suggestions.push(`What is the highest ${this.formatColumnName(numericCols[0])}?`);
    }
    
    if (categoricalCols.length > 0) {
      suggestions.push(`What is the most common ${this.formatColumnName(categoricalCols[0])}?`);
      suggestions.push(`How many different ${this.formatColumnName(categoricalCols[0])} are there?`);
    }
    
    suggestions.push("How many records are in the dataset?");
    
    return suggestions.slice(0, 3);
  }

  generateQuestionSuggestions(): QuestionSuggestion[] {
    const suggestions: QuestionSuggestion[] = [];
    const numericCols = this.getNumericColumns();
    const categoricalCols = this.getCategoricalColumns();
    const dateCols = this.getDateColumns();

    // Basic questions
    suggestions.push({
      category: 'Overview',
      icon: '📊',
      questions: [
        'How many records are in the dataset?',
        'What columns are available?',
        'What is the data about?'
      ]
    });

    // Numeric analysis
    if (numericCols.length > 0) {
      const primaryNumeric = numericCols[0];
      const colName = this.formatColumnName(primaryNumeric);
      
      suggestions.push({
        category: 'Numbers',
        icon: '🔢',
        questions: [
          `What is the average ${colName}?`,
          `What is the highest ${colName}?`,
          `What is the lowest ${colName}?`
        ]
      });
    }

    // Categorical analysis
    if (categoricalCols.length > 0) {
      const primaryCategorical = categoricalCols[0];
      const colName = this.formatColumnName(primaryCategorical);
      
      suggestions.push({
        category: 'Categories',
        icon: '🏷️',
        questions: [
          `What is the most common ${colName}?`,
          `How many different ${colName} are there?`,
          `Show me the distribution of ${colName}`
        ]
      });
    }

    // Time analysis
    if (dateCols.length > 0 && numericCols.length > 0) {
      const valueCol = this.formatColumnName(numericCols[0]);
      
      suggestions.push({
        category: 'Trends',
        icon: '📈',
        questions: [
          `What is the trend over time?`,
          `How has ${valueCol} changed over time?`,
          `What was the growth rate?`
        ]
      });
    }

    return suggestions;
  }
}
